import { useState, useEffect, useCallback } from 'react';
import { Toast } from '@douyinfe/semi-ui';
import { API } from '../helpers';

const APIKEY_STORAGE_KEY = 'neko_apikey';

export const useTokenQuery = () => {
    const [apikey, setAPIKey] = useState(() => {
        return localStorage.getItem(APIKEY_STORAGE_KEY) || '';
    });
    
    const [loading, setLoading] = useState(false);
    const [activeTabKey, setActiveTabKey] = useState('');
    const [baseUrl, setBaseUrl] = useState('');
    const [tabData, setTabData] = useState({});
    
    // 解析环境变量中的 BaseURLs，默认为 aug.jispul.com
    const baseUrls = JSON.parse(process.env.REACT_APP_BASE_URL || '{"API": "https://aug.jispul.com"}');

    // 初始化 BaseUrl
    useEffect(() => {
        const keys = Object.keys(baseUrls);
        if (keys.length > 0) {
            const firstKey = keys[0];
            setActiveTabKey(firstKey);
            setBaseUrl(baseUrls[firstKey]);
        }
    }, []);

    const handleTabChange = (key) => {
        setActiveTabKey(key);
        setBaseUrl(baseUrls[key]);
    };

    const resetData = useCallback((key) => {
        setTabData((prevData) => ({
            ...prevData,
            [key]: {
                balance: 0,
                usage: 0,
                accessdate: "未知",
                logs: [],
                tokenValid: false,
            }
        }));
    }, []);

    const fetchData = async () => {
        if (apikey === '') {
            Toast.warning('请先输入令牌，再进行查询');
            return;
        }
        // 简单格式校验
        if (!/^sk-[a-zA-Z0-9]{48}$/.test(apikey)) {
            Toast.error('令牌格式非法！');
            return;
        }

        setLoading(true);
        // 初始化当前 tab 的数据
        let newTabData = { 
            ...tabData[activeTabKey], 
            balance: 0, 
            usage: 0, 
            accessdate: 0, 
            logs: [], 
            tokenValid: false 
        };

        // 检测是否是 stonefancyx 站点（使用新 API）
        const isStonefancyx = baseUrl.includes('stonefancyx.com');

        try {
            // 1. 查询余额和订阅信息
            if (process.env.REACT_APP_SHOW_BALANCE === "true") {
                if (isStonefancyx) {
                    // stonefancyx 站点使用新 API
                    const tokenInfoRes = await API.get(`${baseUrl}/usage/api/token/info`, {
                        headers: { Authorization: `Bearer ${apikey}` },
                    });
                    const tokenData = tokenInfoRes.data;
                    if (tokenData.success) {
                        const data = tokenData.data;
                        // 无限额度显示为 100000000，否则计算实际额度
                        newTabData.balance = data.unlimited_quota ? 100000000 : (data.remain_quota + data.used_quota) / 500000;
                        newTabData.usage = data.used_quota / 500000;
                        newTabData.accessdate = data.expired_time === -1 ? 0 : data.expired_time; // -1 表示永不过期
                        newTabData.tokenValid = true;
                        newTabData.callCount = data.call_count; // 额外保存调用次数
                        newTabData.models = data.models; // 额外保存可用模型
                    } else {
                        throw new Error('Token info query failed');
                    }
                } else {
                    // 原有 API 逻辑
                    const subscription = await API.get(`${baseUrl}/v1/dashboard/billing/subscription`, {
                        headers: { Authorization: `Bearer ${apikey}` },
                    });
                    const subscriptionData = subscription.data;
                    newTabData.balance = subscriptionData.hard_limit_usd;
                    newTabData.accessdate = subscriptionData.access_until; // 读取到期时间
                    newTabData.tokenValid = true;

                    // 查询用量
                    let now = new Date();
                    let start = new Date(now.getTime() - 100 * 24 * 3600 * 1000); // 过去100天
                    let start_date = `${start.getFullYear()}-${start.getMonth() + 1}-${start.getDate()}`;
                    let end_date = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;

                    const res = await API.get(`${baseUrl}/v1/dashboard/billing/usage?start_date=${start_date}&end_date=${end_date}`, {
                        headers: { Authorization: `Bearer ${apikey}` },
                    });
                    newTabData.usage = res.data.total_usage / 100;
                }

                // 保存有效的 key
                localStorage.setItem(APIKEY_STORAGE_KEY, apikey);
            }
        } catch (e) {
            console.error(e);
            Toast.error("令牌无效或已用尽");
            resetData(activeTabKey);
            setLoading(false);
            return; // 余额查询失败通常意味着 Key 无效，停止后续查询
        }

        try {
            // 2. 查询日志详情
            if (process.env.REACT_APP_SHOW_DETAIL === "true") {
                if (isStonefancyx) {
                    // stonefancyx 站点使用新 API（分页获取最近日志）
                    const logRes = await API.get(`${baseUrl}/usage/api/log/token?page=1`, {
                        headers: { Authorization: `Bearer ${apikey}` },
                    });
                    const { success, data: logData } = logRes.data;

                    if (success && logData) {
                        // 转换数据格式以匹配现有组件
                        newTabData.logs = logData.map(log => ({
                            ...log,
                            model: log.model_name, // 兼容旧字段名
                            quota: log.quota / 500000, // 转换为美元
                        })).reverse();
                    } else {
                        Toast.warning('无法获取详细日志');
                    }
                } else {
                    // 原有 API 逻辑
                    const now = Math.floor(Date.now() / 1000);
                    const sevenDaysAgo = now - 7 * 24 * 60 * 60;
                    const logRes = await API.get(`${baseUrl}/api/log/token?key=${apikey}&start_timestamp=${sevenDaysAgo}&end_timestamp=${now}`);
                    const { success, data: logData } = logRes.data;

                    if (success) {
                        newTabData.logs = logData.reverse();
                    } else {
                        Toast.warning('无法获取详细日志');
                    }
                }
            }
        } catch (e) {
            console.error(e);
            // 日志查询失败不影响余额显示，所以不重置整个数据，只提示
            Toast.warning("日志查询失败");
        }

        setTabData((prevData) => ({
            ...prevData,
            [activeTabKey]: newTabData,
        }));
        setLoading(false);
    };

    const activeTabData = tabData[activeTabKey] || { 
        logs: [], 
        balance: 0, 
        usage: 0, 
        accessdate: "未知", 
        tokenValid: false 
    };

    return {
        apikey,
        setAPIKey,
        loading,
        baseUrls,
        activeTabKey,
        handleTabChange,
        fetchData,
        activeTabData
    };
};

